import { startOfDay, subDays } from 'date-fns';

import { DASHBOARD_TASKS_PATH } from '@modules/dashboard/dashboardAnchors';
import type { Translate } from '@shared/hooks/locale';

export type NotificationInboxMessage = {
  messageId: string;
  opened: boolean;
  sentAt: Date;
  type: string;
  properties: Record<string, unknown>;
};

export type NotificationInboxIcon =
  | 'lead'
  | 'enquiry'
  | 'review'
  | 'tasks'
  | 'transaction'
  | 'listing'
  | 'share'
  | 'valuation'
  | 'map';

export type NotificationInboxItem = {
  id: string;
  icon: NotificationInboxIcon;
  title: string;
  description: string;
  path: string | null;
  opened: boolean;
  sentAt: Date;
};

export type NotificationInboxGroupKey = 'today' | 'yesterday' | 'earlier';

export type NotificationInboxGroup = {
  key: NotificationInboxGroupKey;
  items: NotificationInboxItem[];
};

const readString = (
  properties: Record<string, unknown>,
  key: string,
): string | null => {
  const value = properties[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
};

const readNumber = (
  properties: Record<string, unknown>,
  key: string,
): number | null => {
  const value = properties[key];
  const parsed =
    typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : null;
};

const pathWithId = (prefix: string, id: string | null) =>
  id == null ? null : `${prefix}/${encodeURIComponent(id)}`;

const describeTasks = (
  properties: Record<string, unknown>,
  t: Translate,
): string => {
  const today = readNumber(properties, 'today') ?? 0;
  const overdue = readNumber(properties, 'overdue') ?? 0;
  const parts: string[] = [];
  if (today > 0) {
    parts.push(t('{{today}} today', { today }));
  }
  if (overdue > 0) {
    parts.push(t('{{overdue}} overdue', { overdue }));
  }
  return parts.join(', ');
};

const describeListing = (properties: Record<string, unknown>): string => {
  const body = readString(properties, 'notification_body');
  if (body != null) {
    return body;
  }
  return [
    readString(properties, 'listing_title'),
    readString(properties, 'locality'),
  ]
    .filter((part): part is string => part != null)
    .join(' · ');
};

const joinParts = (parts: (string | null)[]): string =>
  parts.filter((part): part is string => part != null).join(' · ');

const listingPath = (properties: Record<string, unknown>) =>
  pathWithId('/mls', readString(properties, 'lot_id'));

type TypeMapper = (
  properties: Record<string, unknown>,
  t: Translate,
) => Pick<NotificationInboxItem, 'icon' | 'title' | 'description' | 'path'>;

const TYPE_MAPPERS: Record<string, TypeMapper> = {
  new_lead: (properties, t) => ({
    icon: 'lead',
    title: t('New lead'),
    description: t("You've got a new lead"),
    path: pathWithId('/leads', readString(properties, 'lead_id')),
  }),
  new_enquiry: (properties, t) => {
    const contact = readString(properties, 'contact');
    return {
      icon: 'enquiry',
      title: t('New enquiry'),
      description:
        contact == null
          ? t("You've got a new enquiry")
          : t("You've got a new enquiry from {{contact}}", { contact }),
      path: pathWithId('/enquiries', readString(properties, 'enquiry_id')),
    };
  },
  new_review: (properties, t) => {
    const reviewer =
      readString(properties, 'display_name') ??
      readString(properties, 'reviewer_email');
    return {
      icon: 'review',
      title: t('New review'),
      description:
        reviewer == null
          ? t("You've received a new review")
          : t("You've received a new review from {{reviewer}}", { reviewer }),
      path: pathWithId(
        '/visibility/reviews',
        readString(properties, 'review_id'),
      ),
    };
  },
  daily_tasks_summary: (properties, t) => ({
    icon: 'tasks',
    title: t("Today's pending tasks"),
    description: describeTasks(properties, t),
    path: DASHBOARD_TASKS_PATH,
  }),
  daily_transaction_reminder: (properties, t) => {
    const monthsSince = readNumber(properties, 'monthsSince');
    return {
      icon: 'transaction',
      title:
        monthsSince == null
          ? t('Add your latest sale')
          : t('Your last sale was added {{count}} month ago', {
              count: monthsSince,
            }),
      description: t(
        'Have you made a sale recently? Add it and improve your ranking!',
      ),
      path: '/transactions/new',
    };
  },
  mls_new_listing: (properties, t) => ({
    icon: 'listing',
    title: readString(properties, 'notification_title') ?? t('New MLS listing'),
    description: describeListing(properties),
    path: listingPath(properties),
  }),
  mls_share_request: (properties, t) => {
    const lotId = readString(properties, 'lot_id');
    return {
      icon: 'share',
      title: t('New MLS share request'),
      description: joinParts([
        readString(properties, 'requester_name'),
        readString(properties, 'listing_title'),
      ]),
      path:
        lotId == null
          ? null
          : `/dashboard/listings/${encodeURIComponent(
              lotId,
            )}/marketing/mls-network`,
    };
  },
  mls_share_accepted: (properties, t) => ({
    icon: 'share',
    title: t('MLS share request accepted'),
    description: joinParts([
      readString(properties, 'owner_name'),
      readString(properties, 'listing_title'),
    ]),
    path: listingPath(properties),
  }),
  mls_share_declined: (properties, t) => ({
    icon: 'share',
    title: t('MLS share request declined'),
    description: joinParts([
      readString(properties, 'owner_name'),
      readString(properties, 'listing_title'),
    ]),
    path: listingPath(properties),
  }),
  property_revaluation: (properties, t) => ({
    icon: 'valuation',
    title: t('Lead valuation updated'),
    description: joinParts([
      readString(properties, 'contact'),
      readString(properties, 'property_address'),
    ]),
    path: pathWithId('/leads', readString(properties, 'lead_id')),
  }),
  prospection_map_new_leads: (properties, t) => {
    const count = readNumber(properties, 'count');
    return {
      icon: 'map',
      title: t('New leads on your prospection map'),
      description:
        count == null || count <= 0
          ? ''
          : t('{{count}} new lead near your prospection area', { count }),
      path: '/prospection',
    };
  },
};

export const getNotificationInboxItem = (
  message: NotificationInboxMessage,
  t: Translate,
): NotificationInboxItem | null => {
  const mapper = TYPE_MAPPERS[message.type];
  if (mapper == null) {
    return null;
  }
  return {
    id: message.messageId,
    opened: message.opened,
    sentAt: message.sentAt,
    ...mapper(message.properties ?? {}, t),
  };
};

export const getNotificationInboxItems = (
  messages: readonly NotificationInboxMessage[],
  t: Translate,
): NotificationInboxItem[] =>
  messages
    .map(message => getNotificationInboxItem(message, t))
    .filter((item): item is NotificationInboxItem => item != null)
    .sort((a, b) => b.sentAt.getTime() - a.sentAt.getTime());

export const countUnreadNotificationInboxItems = (
  items: readonly NotificationInboxItem[],
): number => items.filter(item => !item.opened).length;

export const groupNotificationInboxItems = (
  items: readonly NotificationInboxItem[],
  now: Date,
): NotificationInboxGroup[] => {
  const todayStart = startOfDay(now).getTime();
  const yesterdayStart = startOfDay(subDays(now, 1)).getTime();
  const groups: NotificationInboxGroup[] = [
    { key: 'today', items: [] },
    { key: 'yesterday', items: [] },
    { key: 'earlier', items: [] },
  ];
  items.forEach(item => {
    const day = startOfDay(item.sentAt).getTime();
    if (day >= todayStart) {
      groups[0].items.push(item);
    } else if (day >= yesterdayStart) {
      groups[1].items.push(item);
    } else {
      groups[2].items.push(item);
    }
  });
  return groups.filter(group => group.items.length > 0);
};

export type OpenableNotificationInboxMessage = {
  messageId: string;
  opened: boolean;
  markOpened: () => Promise<void>;
};

export const selectMessagesToOpen = <
  Message extends OpenableNotificationInboxMessage,
>(
  messages: readonly Message[],
  ids: readonly string[],
  locallyOpened: ReadonlySet<string>,
): Message[] =>
  messages.filter(
    message =>
      ids.includes(message.messageId) &&
      !message.opened &&
      !locallyOpened.has(message.messageId),
  );

export const describeError = (error: unknown): string => {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error, null, 2) ?? String(error);
  } catch {
    return String(error);
  }
};

export type MarkOpenedResult = { failedIds: string[]; reasons: string[] };

export const markMessagesOpened = async (
  messages: readonly OpenableNotificationInboxMessage[],
): Promise<MarkOpenedResult> => {
  const results = await Promise.allSettled(
    messages.map(message => message.markOpened()),
  );
  const failedIds: string[] = [];
  const reasons: string[] = [];
  results.forEach((result, index) => {
    if (result.status === 'rejected') {
      failedIds.push(messages[index].messageId);
      reasons.push(describeError(result.reason));
    }
  });
  return { failedIds, reasons };
};

export const dropConfirmedIds = (
  current: ReadonlySet<string>,
  messages: readonly { messageId: string; opened: boolean }[],
): Set<string> =>
  new Set(
    [...current].filter(
      id =>
        !messages.some(message => message.messageId === id && message.opened),
    ),
  );

export const addIds = (
  current: ReadonlySet<string>,
  ids: readonly string[],
): Set<string> => new Set([...current, ...ids]);

export const removeIds = (
  current: ReadonlySet<string>,
  ids: readonly string[],
): Set<string> => new Set([...current].filter(id => !ids.includes(id)));

export const selectUnopenedIds = (
  items: readonly NotificationInboxItem[],
): string[] => items.filter(item => !item.opened).map(item => item.id);

export const retry = async <T>(
  attempt: () => Promise<T> | T,
  {
    attempts,
    delayMs,
    isCancelled,
  }: { attempts: number; delayMs: number; isCancelled: () => boolean },
): Promise<T> => {
  for (let count = 1; ; count += 1) {
    try {
      return await attempt();
    } catch (error) {
      if (count >= attempts || isCancelled()) {
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }
};

export const withTimeout = <T>(
  promise: PromiseLike<T>,
  timeoutMs: number,
  message: string,
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), timeoutMs);
    promise.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      error => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
